import type { FragmentType } from '@packages/gql/generated/shopping';
import { useAbTesting, useMousemovement } from '@packages/abtesting';
import { getFragmentData } from '@packages/gql/generated/shopping';
import type { GtmProduct } from '@packages/tracking';
import { PageMetaData } from '@packages/modules';
import type { FC } from 'react';
import { useEffect } from 'react';
import { TrackingBreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/TrackingBreadcrumbsFragmentFragmentDoc';
import { TrackingFragmentFragmentDoc } from '@packages/gql/generated/shopping/TrackingFragmentFragmentDoc';
import { getCategoryData } from './helper/getCategoryData';
import { useDefaultTracking } from './hooks/useDefaultTracking';
import { useAddLastSeen } from '../../hooks/useAddLastSeen';

/* GraphQL */ `
  fragment TrackingFragment on Product {
    akl
    sku
    marketcode
    pbk
    orderNumber
    ...GetSizeAndVariantFromDimensionsFragment
    availability {
      ...GetDeterminedAvailabilityFragment
    }
    ...UseDefaultTrackingFragment
    ...GetGlycerinDisplayProductPayloadFragment
  }
`;

/* GraphQL */ `
  fragment TrackingBreadcrumbsFragment on Breadcrumb {
    ...GetCategoryDataFragment
  }
`;

export type TrackingProps = {
  breadCrumbData: FragmentType<typeof TrackingBreadcrumbsFragmentFragmentDoc> | undefined | null;
  productData: FragmentType<typeof TrackingFragmentFragmentDoc>;
  navq?: GtmProduct['category'];
};

/**
 * Tracking component used to track
 * */
export const Tracking: FC<TrackingProps> = ({ navq, productData, breadCrumbData }) => {
  const { setDefaultViewOutcome, setOutcome } = useAbTesting();
  const { mouseMoved } = useMousemovement();

  const unmaskedData = getFragmentData(TrackingFragmentFragmentDoc, productData);

  const { akl, orderNumber, sku, pbk, marketcode } = unmaskedData;

  useAddLastSeen(orderNumber);

  const { categoryPath, categoryPathAsString } = getCategoryData(
    getFragmentData(TrackingBreadcrumbsFragmentFragmentDoc, breadCrumbData),
  );

  const { trackGtmProductView, trackGlycerinDisplayProduct, trackGtmPageNavigation } =
    useDefaultTracking();

  useEffect(() => {
    trackGtmProductView({ data: unmaskedData, navq, categoryPathAsString });
    // Glycerin Stuff
    // product event for glycerin tracking
    trackGlycerinDisplayProduct(unmaskedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  /* PDVPageNavigation event: new product loaded */
  useEffect(() => {
    if (akl) {
      trackGtmPageNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl]);

  // lackmus framework default view tracking for abtesting
  useEffect(() => {
    setDefaultViewOutcome(sku, akl, 'RPP');

    // block can be removed after abc test
    const testid = '2023_09_reco_sequence';
    setOutcome(testid, { pbk });
    if (marketcode !== null) setOutcome(testid, { marketcode });
    // end of block

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl, sku]);

  // lackmus framework default tracking for abtesting - can be removed after abc test
  useEffect(() => {
    if (mouseMoved) {
      setOutcome('2023_09_reco_sequence', { MOUSEMOVEMENT: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mouseMoved]);

  return (
    <PageMetaData
      page={{ type: 'detailview', template: 'detailview' }}
      categoryPath={categoryPath}
    />
  );
};
